import {
  buildDriveFlowViewModel,
  type DriveFlowViewModel,
  type DriveLaneViewModel,
} from './driveLaneAggregation';
import {
  adaptDriveHiFiModelForLiveShape,
  isSubstrateSection,
  nestOrphanLeavesUnderTypedLanes,
} from './driveLiveShapeAdapter';
import type { DriveDomainSection, DriveHiFiModel, DriveSubdomainNode } from './types';
import { normalizeScopeIdKey, scopeIdsEqual } from '../../../utils/scopeIdPlane';

export interface DriveSectionFlowViewModel {
  section: DriveDomainSection;
  lanes: DriveLaneViewModel[];
}

export interface DriveHierarchicalFlowViewModel extends DriveFlowViewModel {
  sectionGroups: DriveSectionFlowViewModel[];
  hasSections: boolean;
}

function subdomainById(subdomains: DriveSubdomainNode[]): Map<string, DriveSubdomainNode> {
  return new Map(subdomains.map((sub) => [sub.id, sub]));
}

/** Tier lanes render under product parent on drill — not as top-level columns. */
export function isNestedDriveLane(sub: DriveSubdomainNode): boolean {
  if (sub.laneMode === 'product_tier') return true;
  if (sub.parentProductLaneId) return true;
  return false;
}

/** Wire product-tier grandchildren from manifest tierLaneIds / parentProductLaneId. */
export function enrichDriveSubdomains(subdomains: DriveSubdomainNode[]): DriveSubdomainNode[] {
  const byId = subdomainById(subdomains);
  return subdomains.map((sub) => {
    const tierIds = sub.tierLaneIds ?? [];
    const tierChildren = tierIds
      .map((id) => byId.get(id))
      .filter((child): child is DriveSubdomainNode => Boolean(child))
      .map((child) => ({ id: child.id, name: child.name }));

    const parentTierChildren = subdomains
      .filter((child) => child.parentProductLaneId === sub.id)
      .map((child) => ({ id: child.id, name: child.name }));

    const grandchildren =
      tierChildren.length > 0
        ? tierChildren
        : parentTierChildren.length > 0
          ? parentTierChildren
          : sub.grandchildren;

    if (!grandchildren?.length) return sub;
    return { ...sub, grandchildren };
  });
}

function topLevelLaneIds(model: DriveHiFiModel): Set<string> {
  const ids = new Set<string>();
  for (const section of model.sections ?? []) {
    for (const laneId of section.laneIds ?? []) {
      ids.add(laneId);
    }
  }
  return ids;
}

function grandchildLaneIds(subdomains: DriveSubdomainNode[]): Set<string> {
  const ids = new Set<string>();
  for (const sub of subdomains) {
    for (const child of sub.grandchildren ?? []) {
      ids.add(child.id);
    }
  }
  return ids;
}

/**
 * Prepare subdomains for overview: product-tier wiring + live-shape orphan nesting.
 * Returns enriched rows and ids that should stay off the overview (nested leaves).
 */
export function prepareOverviewSubdomains(model: DriveHiFiModel): {
  subdomains: DriveSubdomainNode[];
  nestedLeafIds: Set<string>;
} {
  const adapted = adaptDriveHiFiModelForLiveShape(model);
  const enriched = enrichDriveSubdomains(adapted.subdomains);
  const { subdomains, nestedLeafIds } = nestOrphanLeavesUnderTypedLanes(
    enriched,
    adapted.sections
  );
  return { subdomains, nestedLeafIds };
}

/** Lanes that appear as columns in the overview (excludes nested product tiers / orphan leaves). */
export function overviewSubdomains(model: DriveHiFiModel): DriveSubdomainNode[] {
  const adapted = adaptDriveHiFiModelForLiveShape(model);
  const { subdomains, nestedLeafIds } = prepareOverviewSubdomains(adapted);
  const sectionLaneIds = topLevelLaneIds(adapted);
  const nestedGrandchildren = grandchildLaneIds(subdomains);

  return subdomains.filter((sub) => {
    if (isNestedDriveLane(sub)) return false;
    if (nestedLeafIds.has(sub.id) || nestedGrandchildren.has(sub.id)) return false;
    if (sectionLaneIds.size > 0 && !sectionLaneIds.has(sub.id)) {
      // Substrate sections list domain id as the only laneId — keep domain-section members.
      if (sub.domainSectionId) {
        const parentSection = (adapted.sections ?? []).find((s) => s.id === sub.domainSectionId);
        if (parentSection && isSubstrateSection(parentSection)) return true;
        return true;
      }
      return false;
    }
    return true;
  });
}

function lanesForSection(
  section: DriveDomainSection,
  flow: DriveFlowViewModel,
  subdomains: DriveSubdomainNode[]
): DriveLaneViewModel[] {
  const laneIdSet = new Set(section.laneIds ?? []);
  const byDomainSection = new Set(
    subdomains.filter((sub) => sub.domainSectionId === section.id).map((sub) => sub.id)
  );

  // Domain-as-lane / substrate: section.laneIds often equals [section.id] with no matching column.
  if (isSubstrateSection(section) || (laneIdSet.size === 1 && laneIdSet.has(section.id))) {
    return flow.lanes.filter(
      (lane) => byDomainSection.has(lane.id) || lane.id === section.id
    );
  }

  const subdomainIds = new Set(
    subdomains
      .filter((sub) => sub.domainSectionId === section.id || laneIdSet.has(sub.id))
      .map((sub) => sub.id)
  );
  if (laneIdSet.size > 0) {
    laneIdSet.forEach((laneId) => {
      subdomainIds.add(laneId);
    });
  }
  return flow.lanes.filter((lane) => subdomainIds.has(lane.id));
}

export function buildDriveHierarchicalFlowViewModel(
  model: DriveHiFiModel
): DriveHierarchicalFlowViewModel {
  const adapted = adaptDriveHiFiModelForLiveShape(model);
  const scopedModel: DriveHiFiModel = {
    ...adapted,
    subdomains: overviewSubdomains(adapted),
  };
  const flow = buildDriveFlowViewModel(scopedModel);
  const sections = adapted.sections ?? [];

  if (!sections.length) {
    return { ...flow, sectionGroups: [], hasSections: false };
  }

  const sectionGroups: DriveSectionFlowViewModel[] = sections
    .map((section) => ({
      section,
      lanes: lanesForSection(section, flow, scopedModel.subdomains),
    }))
    .filter((group) => group.lanes.length > 0);

  return {
    ...flow,
    sectionGroups,
    hasSections: sectionGroups.length > 0,
  };
}

export function scopeDriveSections(
  sections: DriveDomainSection[] | undefined,
  allowedLaneIds: Set<string>
): DriveDomainSection[] | undefined {
  if (!sections?.length) return sections;
  const allowedHas = (id: string | undefined) => {
    const key = normalizeScopeIdKey(id);
    return Boolean(key && allowedLaneIds.has(key));
  };
  const scoped: DriveDomainSection[] = [];
  for (const section of sections) {
    if (isSubstrateSection(section)) {
      // Keep substrate section when any allowed lane belongs to it (domain id or children).
      const laneIds = (section.laneIds ?? []).filter(
        (id) => allowedHas(id) || scopeIdsEqual(id, section.id)
      );
      let hasChild = false;
      const sectionNorm = normalizeScopeIdKey(section.id);
      allowedLaneIds.forEach((id) => {
        if (sectionNorm && (id.startsWith(sectionNorm) || id.startsWith(section.id))) {
          hasChild = true;
        }
      });
      if (laneIds.length || hasChild || allowedHas(section.id)) {
        scoped.push({
          ...section,
          laneIds: laneIds.length ? laneIds : section.laneIds,
        });
      }
      continue;
    }
    const laneIds = (section.laneIds ?? []).filter((id) => allowedHas(id));
    if (laneIds.length) {
      scoped.push({ ...section, laneIds });
    }
  }
  return scoped.length ? scoped : undefined;
}
